import { useState } from "react";
import { Reveal } from "@components/ui";

interface QAItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  name: string;
  items: QAItem[];
}

const faqCategories: FAQCategory[] = [
  {
    name: "Billing & Payments",
    items: [
      {
        question: "How do I pay my gas bill online?",
        answer:
          'You can pay instantly via our secure portal using credit/debit card or bank transfer. Visit Pay Bill under "Online Services".',
      },
      {
        question: "Can I pay without logging in?",
        answer:
          "Yes. Use Quick Pay — just enter your Account Number or Invoice Number and pay instantly.",
      },
      {
        question: "What payment methods are accepted?",
        answer:
          "We accept Visa, MasterCard, Apple Pay, and UAE bank transfers.",
      },
      {
        question: "How do I know my payment was successful?",
        answer:
          "You'll see an on-screen confirmation and receive an email from help@gasis.ae within minutes.",
      },
      {
        question: "Can I set up automatic payments?",
        answer:
          "Yes, you can enable Auto-Debit via your account settings.",
      },
      {
        question: "Are there late-payment fees?",
        answer:
          "If payment is not received within 14 days of the due date, a 5% late-fee may apply.",
      },
      {
        question: "What if my bill seems too high?",
        answer:
          "Check if your meter reading was estimated. You can submit your actual reading anytime via Submit Reading for adjustment.",
      },
      {
        question: "Can I download previous bills?",
        answer: 'Yes. Log into "My Account" → Billing History.',
      },
      {
        question: "What is VAT applied on gas services?",
        answer: "All charges are subject to 5% VAT per UAE law.",
      },
      {
        question: "How can I get a refund for overpayment?",
        answer:
          "Email help@gasis.ae with proof of payment; refunds are processed within 7–10 business days.",
      },
      {
        question: "Can I change the name on the bill?",
        answer:
          "Yes. Submit a Move-In/Out request with the new occupant's documents.",
      },
    ],
  },
  { name: "Meter Reading", items: [] },
  { name: "New Connections", items: [] },
  { name: "Moving In/Out", items: [] },
  { name: "Safety & Emergency", items: [] },
  { name: "Maintenance", items: [] },
  { name: "Business", items: [] },
  { name: "Mobile & Online", items: [] },
  { name: "Policies & Legal", items: [] },
];

function QAAccordionItem({ question, answer }: QAItem) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 ${
        isOpen
          ? "bg-white border-[#f24d2b]/20 shadow-[0_2px_16px_rgba(242,77,43,0.06)]"
          : "bg-white border-[#e9e8e6] hover:border-[#d0d0d0] hover:shadow-sm"
      }`}
    >
      <button
        className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span
          className={`text-[16px] md:text-[20px] lg:text-[24px] font-semibold tracking-[-0.04em] leading-[1.3] transition-colors duration-200 ${
            isOpen ? "text-[#f24d2b]" : "text-[#1e1f24]"
          }`}
        >
          {question}
        </span>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
            isOpen ? "bg-[#f24d2b] rotate-180" : "bg-[#f1f1f1]"
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="transition-colors duration-200"
          >
            <path
              d="M3.5 5.25L7 8.75L10.5 5.25"
              stroke={isOpen ? "white" : "#1e1f24"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
            <div className="w-full h-px bg-[#f0efed] mb-4" />
            <p className="text-[14px] md:text-[18px] lg:text-[20px] text-[#62636c] leading-[1.6] tracking-[-0.04em]">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FAQAccordionSection() {
  const [activeCategory, setActiveCategory] = useState(0);
  const currentCategory = faqCategories[activeCategory];

  return (
    <section className="w-full bg-[#fefcfc] px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <Reveal direction="up">
          <h2 className="text-[28px] md:text-[44px] lg:text-[60px] font-semibold text-[#122d63] tracking-[-0.04em] text-center mb-3 md:mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-[14px] md:text-[18px] lg:text-[20px] text-[#62636c] tracking-[-0.04em] text-center mb-8 md:mb-12 lg:mb-14 max-w-[600px] mx-auto">
            Find answers to common questions about our services, billing, and
            support.
          </p>
        </Reveal>

        {/* Category Tabs */}
        <Reveal direction="up">
          <div className="flex gap-2 md:gap-3 flex-wrap justify-center mb-8 md:mb-12">
            {faqCategories.map((cat, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-[13px] md:text-[15px] font-medium tracking-[-0.02em] transition-all duration-200 whitespace-nowrap ${
                  activeCategory === index
                    ? "bg-[#122d63] text-white shadow-md"
                    : "bg-white text-[#62636c] border border-[#e5e5e5] hover:border-[#c0c0c0] hover:text-[#1e1f24] hover:shadow-sm"
                }`}
              >
                {cat.name}
                {cat.items.length > 0 && (
                  <span
                    className={`ml-1.5 text-[11px] md:text-[12px] px-1.5 py-0.5 rounded-full ${
                      activeCategory === index
                        ? "bg-white/20 text-white"
                        : "bg-[#f1f1f1] text-[#999]"
                    }`}
                  >
                    {cat.items.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Q&A Items */}
        <div className="flex flex-col gap-3 md:gap-4 max-w-[900px] mx-auto">
          {currentCategory.items.length > 0 ? (
            currentCategory.items.map((item, index) => (
              <Reveal key={`${activeCategory}-${index}`} direction="up" delay={index * 40}>
                <QAAccordionItem {...item} />
              </Reveal>
            ))
          ) : (
            <Reveal direction="up">
              <div className="flex flex-col items-center justify-center py-16 md:py-20">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#f6f6f9] flex items-center justify-center mb-5">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#b9bbc6]"
                  >
                    <path
                      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9C15 10.3062 14.1652 11.4175 13 11.8293V13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle cx="12" cy="16" r="1" fill="currentColor" />
                  </svg>
                </div>
                <p className="text-[18px] md:text-[22px] font-semibold text-[#122d63] tracking-[-0.04em] mb-2">
                  Coming Soon
                </p>
                <p className="text-[14px] md:text-[16px] text-[#999] tracking-[-0.04em]">
                  We're preparing answers for this category.
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
